import {
  AppBar,
  Button,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Toolbar,
  Typography,
} from '@mui/material';
// @mui
import { useTheme } from '@mui/material/styles';
import { useAuthContext } from '../../../auth/useAuthContext';
import Iconify from '../../../components/iconify';
// components
import Logo from '../../../components/logo';
import { useSettingsContext } from '../../../components/settings';
// config
import { HEADER, NAV } from '../../../config-global';
// hooks
import useOffSetTop from '../../../hooks/useOffSetTop';
import useResponsive from '../../../hooks/useResponsive';
// utils
import { bgBlur } from '../../../utils/cssStyles';
import AccountPopover from './AccountPopover';
import ContactsPopover from './ContactsPopover';
import LanguagePopover from './LanguagePopover';
import NotificationsPopover from './NotificationsPopover';
//
import Searchbar from './Searchbar';
import SitePopover from './SitePopover';
import { useLocales } from 'src/locales';
import { searchProductsByBarcode } from 'src/redux/slices/product';
import { PATH_DASHBOARD } from 'src/routes/paths';
import { useNavigate } from 'react-router';
import { useState } from 'react';
import { hasPermission, PERMISSIONS, ROLES } from 'src/utils/permissions';

// ----------------------------------------------------------------------

type Props = {
  onOpenNav?: VoidFunction;
};

export default function Header({ onOpenNav }: Props) {
  const theme = useTheme();
  const { user } = useAuthContext();

  const { themeLayout } = useSettingsContext();

  const isNavHorizontal = themeLayout === 'horizontal';

  const isNavMini = themeLayout === 'mini';

  const isDesktop = useResponsive('up', 'lg');

  const isOffset = useOffSetTop(HEADER.H_DASHBOARD_DESKTOP) && !isNavHorizontal;

  const { translate } = useLocales();

  const navigate = useNavigate();

  const [search, setSearch] = useState<{ value: string; error: boolean }>({
    value: '',
    error: false,
  });

  const onSearch = async () => {
    try {
      const result = await searchProductsByBarcode(search.value, false);
      if (result.data.length) {
        setSearch((prevSearch) => ({
          ...prevSearch,
          error: false,
        }));
        navigate(PATH_DASHBOARD.eCommerce.edit(result.data[0].id));
      } else {
        setSearch((prevSearch) => ({
          ...prevSearch,
          error: true,
        }));
      }
    } catch (error) {
      console.error('Barcode search error', error);
    }
  };

  const renderContent = (
    <>
      {isDesktop && isNavHorizontal && <Logo sx={{ mr: 2.5 }} />}

      {!isDesktop && (
        <IconButton onClick={onOpenNav} sx={{ mr: 1, color: 'text.primary' }}>
          <Iconify icon="eva:menu-2-fill" />
        </IconButton>
      )}

      {/* <Searchbar />  */}

      {isDesktop &&
      (ROLES.ADMIN.label === user?.role ||
        hasPermission(user, PERMISSIONS.productCreate) ||
        hasPermission(user, PERMISSIONS.productEdit)) ? (
        <Stack
          width={'100%'}
          display={'flex'}
          flexDirection={'row'}
          gap={'10px'}
          justifyContent={'center'}
          alignItems={'center'}
        >
          <TextField
            error={search.error}
            size="small"
            value={search.value}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setSearch((prevSearch) => ({
                ...prevSearch,
                value: event.target.value,
              }));
            }}
            onKeyUp={(key) => {
              if (key.code === 'Enter' || key.code === 'NumpadEnter') {
                onSearch();
              }
            }}
            placeholder={`${translate('header.placeholder')}`}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Iconify
                    icon="eva:search-fill"
                    sx={{ color: 'text.disabled' }}
                  />
                </InputAdornment>
              ),
            }}
          />
          <Stack position={'relative'}>
            <Button
              variant="contained"
              sx={{
                flexShrink: 0,
                backgroundColor: 'black',
                color: 'white',
                '&:hover': { backgroundColor: 'rgba(0,0,0, 0.7)' },
              }}
              color="inherit"
              onClick={() => onSearch()}
            >
              {`${translate('header.searchButton')}`}
            </Button>
            {search.error && (
              <Typography
                position="absolute"
                top="50%"
                left="100%"
                ml={2}
                color="error"
                width={'100%'}
                sx={{ transform: 'translateY(-50%)' }}
              >
                {`${translate('header.error')}`}
              </Typography>
            )}
          </Stack>
        </Stack>
      ) : (
        <></>
      )}
      <Stack
        flexGrow={1}
        direction="row"
        alignItems="center"
        justifyContent="flex-end"
        spacing={{ xs: 0.5, sm: 1.5 }}
      >
        {user?.site_ids?.length > 1 ? <SitePopover /> : <></>}
        <LanguagePopover />

        <AccountPopover />
      </Stack>
    </>
  );

  return (
    <AppBar
      sx={{
        boxShadow: 'none',
        height: HEADER.H_MOBILE,
        zIndex: theme.zIndex.appBar + 1,
        // ...bgBlur({
        //   color: '#ffffff80',
        // }),
        backdropFilter: 'blur(10px)',
        backgroundColor: '#ffffff80',
        transition: theme.transitions.create(['height'], {
          duration: theme.transitions.duration.shorter,
        }),
        ...(isDesktop && {
          width: `calc(100% - ${NAV.W_DASHBOARD + 1}px)`,
          height: HEADER.H_DASHBOARD_DESKTOP,
          ...(isOffset && {
            height: HEADER.H_DASHBOARD_DESKTOP_OFFSET,
          }),
          ...(isNavHorizontal && {
            width: 1,
            bgcolor: 'background.default',
            height: HEADER.H_DASHBOARD_DESKTOP_OFFSET,
            borderBottom: `dashed 1px ${theme.palette.divider}`,
          }),
          ...(isNavMini && {
            width: `calc(100% - ${NAV.W_DASHBOARD_MINI + 1}px)`,
          }),
        }),
      }}
    >
      <Toolbar
        sx={{
          height: 1,
          px: { lg: 5 },
        }}
      >
        {renderContent}
      </Toolbar>
    </AppBar>
  );
}
